import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Tab,
  Tabs,
  TextField,
  Typography,
  Checkbox,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material'
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid'
import { MdBlock, MdCheckCircle } from 'react-icons/md'
import useAccessControl from '../../hooks/useAccessControl'
import {
  MAIL_ROUTE_KEYS,
  MAIL_ROUTE_LABELS,
  REPORT_KEYS,
  REPORT_LABELS,
  emptyReports,
} from '../../helper/accessControlConstants'

const REPORT_ORDER = [
  REPORT_KEYS.TESEKKUR,
  REPORT_KEYS.ONERITALEP,
  REPORT_KEYS.SIKAYET,
  REPORT_KEYS.RAMAKKALA,
  REPORT_KEYS.ETIKLIK,
  REPORT_KEYS.ODUL_IK,
  REPORT_KEYS.ODUL_OPEX,
]

const formatDate = (value) => {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('tr-TR')
  } catch {
    return String(value)
  }
}

/** Gizli yönetim: /yonetim/erisim — Auth kullanıcıları, rapor yetkileri, mail yönlendirme */
const ErisimYonetimi = () => {
  const {
    permissions,
    mailRoutes,
    loading,
    loadAccessControl,
    savePermission,
    saveMailRoutes,
    listAuthUsers,
    createAuthUser,
    setAuthUserDisabled,
    reseedDefaults,
  } = useAccessControl()

  const [tab, setTab] = useState(0)
  const [authUsers, setAuthUsers] = useState([])
  const [authLoading, setAuthLoading] = useState(false)
  const [authError, setAuthError] = useState('')

  const [createOpen, setCreateOpen] = useState(false)
  const [createEmail, setCreateEmail] = useState('')
  const [createPassword, setCreatePassword] = useState('')
  const [createSaving, setCreateSaving] = useState(false)

  const [permEmail, setPermEmail] = useState('')
  const [permReports, setPermReports] = useState(emptyReports())
  const [permSaving, setPermSaving] = useState(false)

  const [mailDraft, setMailDraft] = useState({})
  const [mailInput, setMailInput] = useState({})
  const [mailSaving, setMailSaving] = useState(false)

  const refreshAuthUsers = useCallback(async () => {
    setAuthLoading(true)
    setAuthError('')
    try {
      const users = await listAuthUsers()
      setAuthUsers(users)
    } catch (error) {
      setAuthError(error?.message || 'Auth listesi alınamadı')
      setAuthUsers([])
    } finally {
      setAuthLoading(false)
    }
  }, [listAuthUsers])

  useEffect(() => {
    loadAccessControl()
  }, [loadAccessControl])

  useEffect(() => {
    if (tab === 0) refreshAuthUsers()
  }, [tab, refreshAuthUsers])

  useEffect(() => {
    setMailDraft(mailRoutes || {})
  }, [mailRoutes])

  const authRows = useMemo(
    () =>
      (authUsers || []).map((u) => ({
        id: u.uid,
        email: u.email,
        disabled: u.disabled,
        createdAt: u.createdAt,
        lastSignIn: u.lastSignIn,
        uid: u.uid,
      })),
    [authUsers]
  )

  const permRows = useMemo(() => {
    const emails = new Set([
      ...Object.keys(permissions || {}),
      ...authUsers.map((u) => String(u.email || '').toLowerCase()).filter(Boolean),
    ])
    return Array.from(emails)
      .sort((a, b) => a.localeCompare(b, 'tr'))
      .map((email) => {
        const reports = permissions[email]?.reports || emptyReports()
        return {
          id: email,
          email,
          ...reports,
        }
      })
  }, [permissions, authUsers])

  const handleCreateUser = async () => {
    const email = createEmail.trim().toLowerCase()
    setCreateSaving(true)
    try {
      await createAuthUser({ email, password: createPassword })
      setCreateOpen(false)
      setCreateEmail('')
      setCreatePassword('')
      await refreshAuthUsers()
      if (email && !permissions[email]) {
        await savePermission(email, emptyReports())
      }
    } catch {
      /* toast in hook */
    } finally {
      setCreateSaving(false)
    }
  }

  const openPermEditor = (email) => {
    const e = String(email || '').toLowerCase()
    setPermEmail(e)
    setPermReports({ ...emptyReports(), ...(permissions[e]?.reports || {}) })
  }

  const handleSavePerm = async () => {
    setPermSaving(true)
    try {
      await savePermission(permEmail, permReports)
    } finally {
      setPermSaving(false)
    }
  }

  const toggleReport = (key) => {
    setPermReports((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const addMailChip = (routeKey, field) => {
    const raw = String(mailInput[`${routeKey}_${field}`] || '')
      .trim()
      .toLowerCase()
    if (!raw || !raw.includes('@')) return
    setMailDraft((prev) => {
      const route = prev[routeKey] || { to: [], cc: [] }
      const list = Array.isArray(route[field]) ? [...route[field]] : []
      if (!list.includes(raw)) list.push(raw)
      return { ...prev, [routeKey]: { ...route, [field]: list } }
    })
    setMailInput((prev) => ({ ...prev, [`${routeKey}_${field}`]: '' }))
  }

  const removeMailChip = (routeKey, field, email) => {
    setMailDraft((prev) => {
      const route = prev[routeKey] || { to: [], cc: [] }
      const list = (route[field] || []).filter((e) => e !== email)
      return { ...prev, [routeKey]: { ...route, [field]: list } }
    })
  }

  const handleSaveMail = async () => {
    setMailSaving(true)
    try {
      await saveMailRoutes(mailDraft)
    } finally {
      setMailSaving(false)
    }
  }

  const authColumns = [
    { field: 'email', headerName: 'E-posta', flex: 1.2, minWidth: 220 },
    {
      field: 'disabled',
      headerName: 'Durum',
      width: 120,
      renderCell: (params) =>
        params.value ? (
          <Chip size="small" label="Pasif" color="default" />
        ) : (
          <Chip size="small" label="Aktif" color="success" variant="outlined" />
        ),
    },
    {
      field: 'createdAt',
      headerName: 'Oluşturulma',
      width: 170,
      valueFormatter: ({ value }) => formatDate(value),
    },
    {
      field: 'lastSignIn',
      headerName: 'Son Giriş',
      width: 170,
      valueFormatter: ({ value }) => formatDate(value),
    },
    { field: 'uid', headerName: 'Kullanıcı UID', flex: 1, minWidth: 200 },
    {
      field: 'actions',
      type: 'actions',
      width: 80,
      getActions: (params) => [
        <GridActionsCellItem
          key="toggle"
          icon={params.row.disabled ? <MdCheckCircle size={20} /> : <MdBlock size={20} />}
          label={params.row.disabled ? 'Aktifleştir' : 'Pasifleştir'}
          onClick={async () => {
            await setAuthUserDisabled({
              uid: params.row.uid,
              disabled: !params.row.disabled,
            })
            refreshAuthUsers()
          }}
        />,
      ],
    },
  ]

  const permColumns = [
    { field: 'email', headerName: 'E-posta', flex: 1.2, minWidth: 220 },
    ...REPORT_ORDER.map((key) => ({
      field: key,
      headerName: REPORT_LABELS[key],
      width: key === REPORT_KEYS.RAMAKKALA ? 160 : 110,
      type: 'boolean',
    })),
  ]

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, mt: { xs: 8, sm: 10 }, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Erişim ve Mail Yönetimi
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Firebase Authentication kullanıcıları, rapor görünürlüğü ve talep mail alıcıları. Bu sayfa
        menüde görünmez; linki bilen giriş yapmış kullanıcılar yönetebilir.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Button variant="outlined" onClick={() => loadAccessControl()} disabled={loading}>
          Yenile
        </Button>
        <Button
          variant="outlined"
          color="warning"
          onClick={() => {
            if (window.confirm('Mevcut seed varsayılanları RTDB üzerine yazılsın mı?')) {
              reseedDefaults()
            }
          }}
        >
          Varsayılanları yükle
        </Button>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Kullanıcılar" />
        <Tab label="Rapor yetkileri" />
        <Tab label="Mail yönlendirme" />
      </Tabs>

      {tab === 0 && (
        <Box>
          {authError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {authError}
            </Alert>
          )}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button variant="contained" onClick={() => setCreateOpen(true)}>
              Kullanıcı Ekle
            </Button>
            <Button variant="outlined" onClick={refreshAuthUsers} disabled={authLoading}>
              Listeyi yenile
            </Button>
            {authLoading && <CircularProgress size={24} />}
          </Stack>
          <Box sx={{ height: 520, width: '100%' }}>
            <DataGrid
              rows={authRows}
              columns={authColumns}
              loading={authLoading}
              disableRowSelectionOnClick
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              onRowDoubleClick={(params) => {
                setTab(1)
                openPermEditor(params.row.email)
              }}
            />
          </Box>
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Satıra tıklayarak düzenleyin. Auth’taki kullanıcılar listede görünür; henüz yetkisi
            olmayanlar boş başlar.
          </Typography>
          <Box sx={{ height: 420, width: '100%', mb: 3 }}>
            <DataGrid
              rows={permRows}
              columns={permColumns}
              loading={loading}
              disableRowSelectionOnClick
              onRowClick={(params) => openPermEditor(params.row.email)}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            />
          </Box>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Yetki düzenle
          </Typography>
          <TextField
            label="E-posta"
            fullWidth
            size="small"
            sx={{ mb: 2, maxWidth: 420 }}
            value={permEmail}
            onChange={(e) => {
              const v = e.target.value
              setPermEmail(v)
              const key = v.trim().toLowerCase()
              if (permissions[key]) {
                setPermReports({ ...emptyReports(), ...permissions[key].reports })
              }
            }}
          />
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }}>
            {REPORT_ORDER.map((key) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={Boolean(permReports[key])}
                    onChange={() => toggleReport(key)}
                  />
                }
                label={REPORT_LABELS[key]}
              />
            ))}
          </Stack>
          <Button
            variant="contained"
            disabled={!permEmail.trim() || permSaving}
            onClick={handleSavePerm}
          >
            {permSaving ? 'Kaydediliyor…' : 'Yetkileri kaydet'}
          </Button>
        </Box>
      )}

      {tab === 2 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Yeni kayıt oluşturulunca bildirim bu Alıcı (To) / Bilgi (Cc) listelerine gider. Teşekkür,
            Şikayet ve Talep eskiden <code>REACT_APP_MAIL_TO_ADDRESS</code> (
            {process.env.REACT_APP_MAIL_TO_ADDRESS || 'tanımsız'}) adresine düşüyordu — burada
            düzenleyebilirsiniz. Alıcı listesi boşsa yine bu env adresi yedek kullanılır.
          </Typography>
          {MAIL_ROUTE_KEYS.map((routeKey) => {
            const route = mailDraft[routeKey] || { to: [], cc: [] }
            const toEmpty = !(route.to && route.to.length)
            return (
              <Box
                key={routeKey}
                sx={{
                  mb: 3,
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Typography fontWeight={600} gutterBottom>
                  {MAIL_ROUTE_LABELS[routeKey]}
                </Typography>
                {toEmpty && (
                  <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
                    Alıcı boş → yedek: {process.env.REACT_APP_MAIL_TO_ADDRESS || '(env yok)'}
                  </Typography>
                )}
                {['to', 'cc'].map((field) => (
                  <Box key={field} sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {field === 'to' ? 'Alıcı (To)' : 'Bilgi (Cc)'}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ my: 0.5 }}>
                      {(route[field] || []).length === 0 ? (
                        <Typography variant="caption" color="text.disabled">
                          (liste boş)
                        </Typography>
                      ) : (
                        (route[field] || []).map((email) => (
                          <Chip
                            key={email}
                            label={email}
                            size="small"
                            onDelete={() => removeMailChip(routeKey, field, email)}
                          />
                        ))
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        placeholder="email@bonna.com.tr"
                        value={mailInput[`${routeKey}_${field}`] || ''}
                        onChange={(e) =>
                          setMailInput((prev) => ({
                            ...prev,
                            [`${routeKey}_${field}`]: e.target.value,
                          }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            addMailChip(routeKey, field)
                          }
                        }}
                        sx={{ maxWidth: 320 }}
                      />
                      <Button size="small" onClick={() => addMailChip(routeKey, field)}>
                        Ekle
                      </Button>
                    </Stack>
                  </Box>
                ))}
              </Box>
            )
          })}
          <Button variant="contained" disabled={mailSaving} onClick={handleSaveMail}>
            {mailSaving ? 'Kaydediliyor…' : 'Mail yönlendirmeyi kaydet'}
          </Button>
        </Box>
      )}

      <Dialog open={createOpen} onClose={() => !createSaving && setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Kullanıcı Ekle</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="E-posta"
            type="email"
            fullWidth
            value={createEmail}
            onChange={(e) => setCreateEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Şifre"
            type="password"
            fullWidth
            helperText="En az 6 karakter"
            value={createPassword}
            onChange={(e) => setCreatePassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={createSaving}>
            İptal
          </Button>
          <Button variant="contained" onClick={handleCreateUser} disabled={createSaving}>
            Oluştur
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ErisimYonetimi
